import {
  TERMINAL_STATUSES,
  type ChainCallRecord,
  type ChainExecution,
  type EasyAIFeedbackItem,
  type ExecutionStarted,
  type ModelCatalog,
  type PublishedPlatformModels,
  type RunProgress,
  type TempUpload,
} from "./types";

export class EasyAIError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body?: unknown,
    readonly execution?: ChainExecution,
  ) {
    super(message);
    this.name = "EasyAIError";
  }
}

export interface EasyAIClientOptions {
  /** e.g. `https://easyai.passiolife.com`, or a same-origin proxy such as `/proxy/easyai`. */
  baseUrl: string;
  /** EasyAI API key. Omit when a proxy adds it server-side. */
  apiKey?: string;
  fetch?: typeof fetch;
  credentials?: RequestCredentials;
  /** Called with a fresh snapshot of each `run()` as it starts, on every poll and when it ends. */
  onCall?: (record: ChainCallRecord) => void;
}

export interface RunOptions {
  signal?: AbortSignal;
  onProgress?: (p: RunProgress) => void;
  pollIntervalMs?: number;
  maxPolls?: number;
  /** Shown in `onCall` records. */
  chainKey?: string;
  label?: string;
}

let seq = 0;

export interface RunResult<T> {
  execution: ChainExecution;
  /** Parsed output of the sink (last) node. */
  output: T;
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
    const t = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(t);
        reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
      },
      { once: true },
    );
  });
}

/**
 * Tokens an execution used: `total_tokens`, else the sum of each step's `tokenUsage` (the execution
 * total can be 0 or missing while the steps carry it). `undefined` when nothing reported usage.
 */
export function executionTokens(execution: Pick<ChainExecution, "total_tokens" | "node_results">): number | undefined {
  const total = Number(execution.total_tokens);
  if (total > 0) return total;
  let sum = 0;
  let seen = false;
  for (const r of Object.values(execution.node_results ?? {})) {
    const u = r?.tokenUsage as Record<string, unknown> | null | undefined;
    if (!u || typeof u !== "object") continue;
    const t = Number(u.total_tokens) || (Number(u.input_tokens ?? u.prompt_tokens) || 0) + (Number(u.output_tokens ?? u.completion_tokens) || 0);
    if (t > 0) {
      sum += t;
      seen = true;
    }
  }
  return seen ? sum : undefined;
}

/**
 * Output of the chain's sink node. Picks `sinkNodeId` if given, else the last node, parses
 * JSON strings and unwraps a nested `{ output: {...} }`.
 */
export function sinkOutput<T = unknown>(execution: ChainExecution, sinkNodeId?: string): T | undefined {
  const nr = execution.node_results ?? {};
  const keys = Object.keys(nr);
  const node = (sinkNodeId && nr[sinkNodeId]) || nr[keys[keys.length - 1]];
  if (!node) return undefined;
  let o: unknown = node.output !== undefined ? node.output : node;
  if (typeof o === "string") {
    try {
      o = JSON.parse(o);
    } catch {
      /* plain text output */
    }
  }
  if (o && typeof o === "object" && "output" in o && typeof (o as { output: unknown }).output === "object") {
    o = (o as { output: unknown }).output;
  }
  return o as T;
}

export class EasyAIClient {
  private readonly baseUrl: string;
  private readonly apiKey?: string;
  private readonly fetchImpl: typeof fetch;
  private readonly credentials?: RequestCredentials;
  private readonly onCall?: (record: ChainCallRecord) => void;

  constructor(opts: EasyAIClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/+$/, "");
    this.apiKey = opts.apiKey;
    this.fetchImpl = opts.fetch ?? ((...args) => fetch(...args));
    this.credentials = opts.credentials;
    this.onCall = opts.onCall;
  }

  private async call<T>(method: string, path: string, init: { body?: BodyInit; json?: unknown; signal?: AbortSignal } = {}): Promise<T> {
    const headers: Record<string, string> = { Accept: "application/json" };
    if (this.apiKey) headers.Authorization = `Bearer ${this.apiKey}`;
    if (init.json !== undefined) headers["Content-Type"] = "application/json";
    const res = await this.fetchImpl(`${this.baseUrl}${path}`, {
      method,
      headers,
      body: init.json !== undefined ? JSON.stringify(init.json) : init.body,
      signal: init.signal,
      credentials: this.credentials,
    });
    const text = await res.text();
    let body: unknown = text;
    try {
      body = text ? JSON.parse(text) : undefined;
    } catch {
      /* keep text */
    }
    if (!res.ok) {
      const b = (body && typeof body === "object" ? body : {}) as Record<string, unknown>;
      const msg = [b.detail, b.error, b.message].find((x) => typeof x === "string") as string | undefined;
      throw new EasyAIError(msg ?? `EasyAI HTTP ${res.status}`, res.status, body);
    }
    return body as T;
  }

  /** Start a chain run. Returns as soon as the execution is queued. */
  execute(chainId: string, inputData: Record<string, unknown>, signal?: AbortSignal): Promise<ExecutionStarted> {
    return this.call("POST", `/api/v2/chains/${encodeURIComponent(chainId)}/execute`, { json: { input_data: inputData }, signal });
  }

  getExecution(executionId: string, signal?: AbortSignal): Promise<ChainExecution> {
    return this.call("GET", `/api/v2/chains/executions/${encodeURIComponent(executionId)}`, { signal });
  }

  /** Poll until the execution reaches a terminal status, reporting per-node progress. */
  async waitForExecution(started: ExecutionStarted, opts: RunOptions = {}): Promise<ChainExecution> {
    const interval = opts.pollIntervalMs ?? 1500;
    const max = opts.maxPolls ?? 120;
    const t0 = Date.now();
    opts.onProgress?.({ status: started.status ?? "pending", elapsedMs: 0, polls: 0, nodes: [] });
    for (let i = 1; i <= max; i++) {
      await sleep(interval, opts.signal);
      const exe = await this.getExecution(started.id, opts.signal);
      exe.id ??= started.id;
      opts.onProgress?.({
        status: exe.status,
        elapsedMs: Date.now() - t0,
        polls: i,
        nodes: Object.entries(exe.node_results ?? {}).map(([nodeId, r]) => ({ nodeId, status: r?.status ?? "pending" })),
        execution: exe,
      });
      if (TERMINAL_STATUSES.includes(exe.status)) return exe;
    }
    throw new EasyAIError(`Chain did not finish after ${max} polls`, 0);
  }

  /** Execute + wait + parse the sink output. Throws on failed/cancelled runs. */
  async run<T = unknown>(chainId: string, inputData: Record<string, unknown>, opts: RunOptions & { sinkNodeId?: string } = {}): Promise<RunResult<T>> {
    const t0 = Date.now();
    const record: ChainCallRecord = {
      kind: "chain",
      id: `${t0.toString(36)}-c${(++seq).toString(36)}`,
      chainId,
      chainKey: opts.chainKey,
      label: opts.label,
      url: `${this.baseUrl}/api/v2/chains/${encodeURIComponent(chainId)}/execute`,
      startedAt: new Date(t0).toISOString(),
      durationMs: 0,
      requestBody: { input_data: inputData },
      status: "pending",
      polls: 0,
      sinkNodeId: opts.sinkNodeId,
    };
    const report = (patch: Partial<ChainCallRecord>) => {
      if (!this.onCall) return;
      Object.assign(record, patch, { durationMs: Date.now() - t0 });
      this.onCall({ ...record });
    };
    report({});
    try {
      const started = await this.execute(chainId, inputData, opts.signal);
      report({ executionId: started.id, status: started.status ?? "pending" });
      const execution = await this.waitForExecution(started, {
        ...opts,
        onProgress: (p) => {
          opts.onProgress?.(p);
          if (p.execution) report({ status: p.status, polls: p.polls, execution: p.execution });
        },
      });
      report({ status: execution.status, execution, error: execution.error ?? undefined });
      if (execution.status !== "completed") {
        throw new EasyAIError(execution.error || `Chain ${execution.status}`, 0, undefined, execution);
      }
      return { execution, output: sinkOutput<T>(execution, opts.sinkNodeId) as T };
    } catch (e) {
      if (!(e instanceof EasyAIError && e.execution)) {
        const aborted = e instanceof DOMException && e.name === "AbortError";
        report(
          aborted
            ? { status: "stopped", error: "Stopped waiting. The run may still finish on the server." }
            : { status: "error", error: e instanceof Error ? e.message : String(e), httpStatus: e instanceof EasyAIError && e.status ? e.status : undefined },
        );
      }
      throw e;
    }
  }

  /** Upload a file and get a short-lived public URL to pass as a chain image input. */
  uploadTemp(file: Blob, filename = "upload.jpg", signal?: AbortSignal): Promise<TempUpload> {
    const fd = new FormData();
    fd.append("file", file, filename);
    return this.call("POST", "/api/v2/temp/upload", { body: fd, signal });
  }

  sendFeedback(items: EasyAIFeedbackItem[], signal?: AbortSignal): Promise<unknown> {
    return this.call("POST", "/api/v2/feedback/batch", { json: { items: items.slice(0, 100) }, signal });
  }

  /** Text models with tier, 1-5 ratings, list price and similar models. Public: no API key needed. */
  async modelCatalog(opts: { includeDeprecated?: boolean; signal?: AbortSignal } = {}): Promise<ModelCatalog> {
    const q = opts.includeDeprecated ? "?include_deprecated=true" : "";
    const res = await this.call<{ data?: ModelCatalog } | ModelCatalog>("GET", `/api/v2/models/catalog${q}`, { signal: opts.signal });
    return "models" in res ? res : (res.data ?? { tiers: [], rating_scale: {}, models: [] });
  }

  /** Tested accuracy, speed and cost per model, and the Recommended / High quality / Budget picks, for published platform routes. */
  async publishedPlatformModels(signal?: AbortSignal): Promise<PublishedPlatformModels> {
    const res = await this.call<Partial<PublishedPlatformModels>>("GET", "/api/v2/tests/published/platform-models", { signal });
    return { routes: res.routes ?? [], generated_at: res.generated_at ?? "" };
  }
}
